// Infinite scroll for the collection / search product grid.
//
// Design notes:
// - facets.js re-renders #ProductGridContainer by setting its .innerHTML on
//   every filter/sort/paginate action. That destroys the sentinel and the
//   product-grid <ul>, so we must NOT cache those nodes across renders — we
//   re-query them on every init and watch the (stable) #ProductGridContainer
//   for child changes to re-init.
// - An IntersectionObserver alone is unreliable here: it only fires on a
//   *change* in intersection. If appended items don't push the sentinel out
//   of view, or an intersection event arrives while a fetch is in flight, no
//   further event fires and loading stalls until the user scrolls up/down.
//   We therefore drive loading from the scroll position directly and use the
//   observer only as an extra trigger.

(function () {
  let nextUrl = null;
  let loading = false;
  let productGrid = null;
  let paginationEl = null;
  let observer = null;

  const TRIGGER_OFFSET = 600; // px before the sentinel to start loading

  function createSkeletons() {
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < 4; i++) {
      const li = document.createElement('li');
      li.className = 'grid__item skeleton-card';
      li.innerHTML = `
        <div class="card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner">
              <div class="card__media">
                <div class="media media--transparent" style="padding-bottom: 100%;">
                  <div class="skeleton skeleton-img"><span style="visibility: hidden;">Image</span></div>
                </div>
              </div>
            </div>
            <div class="card__content" style="padding-top: 12px;">
              <div class="skeleton skeleton-title"><span style="visibility: hidden;">Title</span></div>
              <div class="skeleton skeleton-price"><span style="visibility: hidden;">Price</span></div>
            </div>
          </div>
        </div>
      `;
      fragment.appendChild(li);
    }
    return fragment;
  }

  function removeSkeletons() {
    document.querySelectorAll('.skeleton-card').forEach((el) => el.remove());
  }

  // Should we load the next page given the sentinel's current position?
  function sentinelInRange() {
    if (!paginationEl) return false;
    const rect = paginationEl.getBoundingClientRect();
    return rect.top <= window.innerHeight + TRIGGER_OFFSET;
  }

  async function loadNextPage() {
    if (!nextUrl || loading || !productGrid) return;
    loading = true;

    productGrid.appendChild(createSkeletons());

    try {
      const res = await fetch(nextUrl);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const newItems = html.querySelectorAll('#ProductGridContainer ul.product-grid > li');

      removeSkeletons();
      newItems.forEach((item) => productGrid.appendChild(item));

      const newPaginate = html.querySelector('#InfiniteScrollPagination');
      nextUrl = newPaginate ? newPaginate.dataset.nextUrl : null;
    } catch (e) {
      removeSkeletons();
      console.error('Infinite scroll failed:', e);
    }

    loading = false;

    // Keep loading while the sentinel is still within range — e.g. when a
    // full page of new items still didn't fill the viewport. Without this the
    // observer would not re-fire and loading would stall.
    if (nextUrl && sentinelInRange()) {
      requestAnimationFrame(loadNextPage);
    }
  }

  function maybeLoad() {
    if (nextUrl && !loading && sentinelInRange()) {
      loadNextPage();
    }
  }

  // Scroll/resize are the reliable drivers; throttle with rAF.
  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      maybeLoad();
    });
  }

  function initInfiniteScroll() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    productGrid = document.querySelector('#ProductGridContainer ul.product-grid');
    paginationEl = document.querySelector('#InfiniteScrollPagination');
    loading = false;

    if (!productGrid || !paginationEl) {
      nextUrl = null;
      return;
    }

    nextUrl = paginationEl.dataset.nextUrl || null;

    // Extra trigger on top of the scroll handler.
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) maybeLoad();
      },
      { rootMargin: TRIGGER_OFFSET + 'px 0px' }
    );
    observer.observe(paginationEl);

    // In case the first page already leaves the sentinel in view.
    maybeLoad();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);

  // Re-init whenever the grid container's contents are replaced. facets.js
  // sets #ProductGridContainer.innerHTML, which is a childList mutation on
  // that element — more reliable than watching for URL changes (which can
  // fire before facets has finished swapping in the new markup).
  function watchGridContainer() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    // Only re-init when facets.js has actually swapped in a NEW grid (our
    // cached productGrid node becomes detached). Our own <li> appends keep
    // productGrid connected, so they are ignored — preventing re-init loops.
    new MutationObserver(() => {
      if (!productGrid || !productGrid.isConnected) {
        initInfiniteScroll();
      }
    }).observe(container, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGridContainer);
  } else {
    watchGridContainer();
  }
})();
